import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const MobileLocationAutocomplete = ({ onLocationSelect, lockLocationField, unlockLocationField, locationLocked, darkMode, currentLocation }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState(currentLocation || '');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setInputValue(currentLocation || '');
  }, [currentLocation]);

  const fetchSuggestions = async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await axios.get('https://api.geoapify.com/v1/geocode/autocomplete', {
        params: {
          text: query,
          apiKey: '26c5ac382af44ebb9040d86d730657ea'
        }
      });

      const filteredSuggestions = response.data.features.map(feature => {
        const { city, state_code } = feature.properties;
        if (city && state_code) {
          return `${city}, ${state_code}`;
        }
        return feature.properties.formatted;
      });

      setSuggestions(filteredSuggestions);
      setHighlightedIndex(-1);
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    fetchSuggestions(value);
  };

  const handleSuggestionClick = (suggestion) => {
    selectSuggestion(suggestion);
  };

  const handleClearClick = () => {
    setInputValue('');
    setSuggestions([]);
    unlockLocationField();
    onLocationSelect(''); // Notify parent component that location has been cleared
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputRef.current) {
        inputRef.current.blur();
      }
      if (highlightedIndex >= 0) {
        selectSuggestion(suggestions[highlightedIndex]);
      } else if (suggestions.length === 1) {
        selectSuggestion(suggestions[0]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (highlightedIndex < suggestions.length - 1) {
        setHighlightedIndex(highlightedIndex + 1);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (highlightedIndex > 0) {
        setHighlightedIndex(highlightedIndex - 1);
      }
    }
  };

  const handleExpand = () => {
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    setIsExpanded(false);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const selectSuggestion = (suggestion) => {
    setInputValue(suggestion);
    setSuggestions([]);
    onLocationSelect(suggestion);
    lockLocationField();
    setHighlightedIndex(-1);
  };

  return (
    <div className={`expandable-input-container ${darkMode ? 'dark-mode' : ''}`}>
      {isExpanded ? (
        <div className="expanded-input">
          <div className="input-wrapper">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="Enter your city"
              className={`input-field-style location-autocomplete-input ${darkMode ? 'dark-mode' : ''} ${isFocused ? 'focused' : ''}`}
              readOnly={locationLocked}
            />
            {suggestions.length > 0 && (
              <ul className={`suggestions-list ${darkMode ? 'dark-mode' : ''}`}>
                {suggestions.map((suggestion, index) => (
                  <li
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className={highlightedIndex === index ? 'highlighted' : ''}
                  >
                    {suggestion}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button onClick={handleCollapse} className={`done-button ${darkMode ? 'dark-mode' : ''}`}>Done</button>
        </div>
      ) : (
        <div className="collapsed-input-wrapper">
          <div className={`collapsed-input ${darkMode ? 'dark-mode' : ''}`} onClick={handleExpand}>
            {inputValue || "Enter your city"}
          </div>
          {locationLocked && (
            <button
              className={`clear-input-button visible ${darkMode ? 'dark-mode' : ''}`}
              onClick={handleClearClick}
              aria-label="Clear input"
            ></button>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileLocationAutocomplete;
